import { useCart } from '@/hooks/useCart';
import type { DeliveryData } from './DeliveryForm';
import type { PaymentData } from './PaymentForm';

interface Props {
  delivery: DeliveryData;
  payment: PaymentData;
}

const DELIVERY_LABELS = { courier: 'Курьером', pickup: 'Самовывоз', post: 'Почта России' };
const PAYMENT_LABELS = { card: 'Банковская карта', sbp: 'СБП', cash: 'Наличными', bonus: 'Бонусами' };

const OrderSummary = ({ delivery, payment }: Props) => {
  const { items, totalPrice } = useCart();
  const savings = items.reduce((s, i) => s + ((i.oldPrice ?? i.price) - i.price) * i.quantity, 0);
  const deliveryCost = delivery.method === 'pickup' ? 0 : totalPrice >= 3000 ? 0 : delivery.method === 'post' ? 299 : 199;
  const bonusDiscount = payment.method === 'bonus' && payment.useBonus ? Math.min(3248, Math.floor(totalPrice * 0.99)) : 0;
  const finalTotal = totalPrice + deliveryCost - bonusDiscount;

  return (
    <div className="space-y-4">
      {/* Items */}
      <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
        <div className="px-5 py-3" style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <p className="font-orbitron font-bold text-xs text-white">Товары ({items.length})</p>
        </div>
        <div className="divide-y" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
          {items.map(item => (
            <div key={item.id} className="flex items-center gap-3 px-5 py-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${item.color}10` }}>
                <img src={item.image} alt={item.name} className="w-8 h-8 object-contain" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-mono text-xs text-white line-clamp-1">{item.name}</p>
                <p className="font-mono text-[10px]" style={{ color: 'rgba(255,255,255,0.3)' }}>× {item.quantity}</p>
              </div>
              <p className="font-orbitron font-bold text-sm shrink-0" style={{ color: item.color }}>
                {(item.price * item.quantity).toLocaleString()} ₽
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Delivery info */}
      <div className="p-4 rounded-xl space-y-2" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <p className="font-orbitron font-bold text-xs text-white mb-3">Доставка</p>
        <Row label="Способ" value={DELIVERY_LABELS[delivery.method]} />
        <Row label="Получатель" value={`${delivery.firstName} ${delivery.lastName}`} />
        <Row label="Телефон" value={delivery.phone} />
        {delivery.method !== 'pickup' && delivery.address && (
          <Row label="Адрес" value={`${delivery.city}, ${delivery.address}${delivery.apartment ? `, кв. ${delivery.apartment}` : ''}`} />
        )}
        {delivery.method === 'pickup' && delivery.pickupPoint && (
          <Row label="Пункт" value={delivery.pickupPoint.split('—')[0].trim()} />
        )}
      </div>

      {/* Payment info */}
      <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
        <p className="font-orbitron font-bold text-xs text-white mb-3">Оплата</p>
        <Row label="Способ" value={PAYMENT_LABELS[payment.method]} />
        {payment.promoApplied && <Row label="Промокод" value={payment.promoCode} highlight />}
      </div>

      {/* Price breakdown */}
      <div className="p-4 rounded-xl space-y-2" style={{ background: 'rgba(255,45,120,0.04)', border: '1px solid rgba(255,45,120,0.12)' }}>
        <Row label="Товары" value={`${totalPrice.toLocaleString()} ₽`} />
        {savings > 0 && <Row label="Скидка на товары" value={`-${savings.toLocaleString()} ₽`} highlight />}
        <Row label="Доставка" value={deliveryCost === 0 ? 'Бесплатно' : `${deliveryCost} ₽`} highlight={deliveryCost === 0} />
        {bonusDiscount > 0 && <Row label="Бонусы" value={`-${bonusDiscount.toLocaleString()} ₽`} highlight />}
        <div className="h-px my-2" style={{ background: 'rgba(255,255,255,0.08)' }} />
        <div className="flex items-center justify-between">
          <span className="font-orbitron font-bold text-sm text-white">ИТОГО</span>
          <span className="font-orbitron font-black text-xl" style={{ color: '#ff2d78', textShadow: '0 0 10px rgba(255,45,120,0.5)' }}>
            {finalTotal.toLocaleString()} ₽
          </span>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) => (
  <div className="flex items-center justify-between">
    <span className="font-mono text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>{label}</span>
    <span className="font-mono text-xs" style={{ color: highlight ? '#39ff14' : 'rgba(255,255,255,0.7)' }}>{value}</span>
  </div>
);

export default OrderSummary;
